import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminNav from '../components/AdminNav';
import {
  getVolunteers, addVolunteer, updateVolunteer, deleteVolunteer,
  getDrivers, addDriver, updateDriver, deleteDriver,
  getCongregations, updateVolunteerApproval
} from '../services/firebase';
import { VEHICLE_MAKES_MODELS, VEHICLE_COLORS } from '../data/vehicleData';
import './Admin.css';

const DAYS = [
  { key: 'thu', label: 'Thu Apr 9',  field: 'thuAvailability' },
  { key: 'fri', label: 'Fri Apr 10', field: 'friAvailability' },
  { key: 'sat', label: 'Sat Apr 11', field: 'satAvailability' },
  { key: 'sun', label: 'Sun Apr 12', field: 'sunAvailability' },
];

const SHIFTS_BY_DAY = {
  thuAvailability: ['7-10 AM', '10 AM-1 PM', '1-3 PM', '3-5 PM', '5-8 PM'],
  friAvailability: ['7-10 AM', '10 AM-1 PM', '1-3 PM', '3-5 PM', '5-8 PM'],
  satAvailability: ['7-10 AM', '10 AM-1 PM', '1-3 PM', '3-5 PM', '5-8 PM'],
  sunAvailability: ['7-10 AM', '10 AM-1 PM', '1-5 PM', '5-8 PM'],
};
// Fallback flat list for filters and sort counts
const SHIFT_TIMES = ['7-10 AM', '10 AM-1 PM', '1-3 PM', '3-5 PM', '1-5 PM', '5-8 PM'];

// Returns count of available shifts for a day field value
function availCount(val) {
  if (!val || val === '' || val === 'Not Available') return 0;
  if (val === 'Anytime') return SHIFT_TIMES.length;
  if (Array.isArray(val)) return val.length;
  return 0;
}

const BLANK_VOLUNTEER = {
  firstName: '', lastName: '', email: '', phone: '', congregation: '',
  volunteerType: 'local', isKeyman: false, isDeafHH: false,
  thuAvailability: '', friAvailability: '', satAvailability: '', sunAvailability: '',
  maritalStatus: '', spouseName: '',
  emergencyContactName: '', emergencyContactPhone: '',
  notes: '',
};

const BLANK_DRIVER = {
  firstName: '', lastName: '', email: '', phone: '', congregation: '',
  vehicleMake: '', vehicleModel: '', vehicleColor: '', vehicleType: '',
  passengerCapacity: '', cartCapacity: '', alsoDoingCartShifts: '',
  thuAvailability: '', friAvailability: '', satAvailability: '', sunAvailability: '',
};

function AvailCell({ val }) {
  if (!val || val === '' || (Array.isArray(val) && val.length === 0)) {
    return <span className="avail-cell avail-none">–</span>;
  }
  if (val === 'Not Available') {
    return <span className="avail-cell avail-no" title="Not Available">✗</span>;
  }
  if (val === 'Anytime') {
    return <span className="avail-cell avail-yes" title="Anytime (all shifts)">All</span>;
  }
  if (Array.isArray(val)) {
    const count = val.length;
    const cls = count >= 5 ? 'avail-yes' : count >= 3 ? 'avail-partial' : 'avail-low';
    return <span className={`avail-cell ${cls}`} title={val.join(', ')}>{count}/6</span>;
  }
  return <span className="avail-cell avail-none">–</span>;
}

// Inline day-availability editor for roster edit/add forms
function DayAvailEditor({ label, value, onChange, dayField }) {
  const shifts = SHIFTS_BY_DAY[dayField] || SHIFT_TIMES;
  // '' = blank/unset (no button highlighted); 'Not Available' = explicitly unavailable
  const mode = value === '' || value === undefined || value === null ? 'blank'
             : value === 'Not Available' ? 'unavailable'
             : value === 'Anytime' ? 'anytime' : 'specific';
  const checked = mode === 'anytime' ? shifts : (Array.isArray(value) ? value : []);

  const setMode = (m) => {
    if (m === 'unavailable') onChange('Not Available');
    else if (m === 'anytime') onChange('Anytime');
    else onChange([]);
  };

  const toggleShift = (shift) => {
    const current = Array.isArray(value) ? value : [];
    const updated = current.includes(shift)
      ? current.filter(s => s !== shift)
      : [...current, shift];
    onChange(updated);
  };

  return (
    <div className="roster-avail-editor">
      <div className="roster-avail-day-label">{label}</div>
      <div className="avail-quick-row" style={{ gap: '4px', marginBottom: '4px' }}>
        <button type="button" className={`avail-quick-btn${mode === 'unavailable' ? ' active-unavail' : ''}`} onClick={() => setMode('unavailable')}>N/A</button>
        <button type="button" className={`avail-quick-btn${mode === 'anytime' ? ' active-avail' : ''}`} onClick={() => setMode('anytime')}>All</button>
        <button type="button" className={`avail-quick-btn${mode === 'specific' ? ' active-specific' : ''}`} onClick={() => setMode('specific')}>Pick…</button>
      </div>
      {(mode === 'specific' || mode === 'anytime') && (
        <div className="avail-checkbox-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '2px 8px' }}>
          {shifts.map(shift => (
            <label key={shift} className="avail-check-item" style={{ fontSize: '12px' }}>
              <input
                type="checkbox"
                checked={checked.includes(shift)}
                onChange={() => {
                  if (mode === 'anytime') onChange(shifts.filter(s => s !== shift));
                  else toggleShift(shift);
                }}
              />
              {shift}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function VolunteerForm({ data, onChange, congregations }) {
  return (
    <div className="roster-edit-form">
      <div className="roster-edit-section">
        <h4>Basic Info</h4>
        <div className="roster-edit-grid">
          <label>First Name *<input value={data.firstName} onChange={e => onChange('firstName', e.target.value)} /></label>
          <label>Last Name *<input value={data.lastName} onChange={e => onChange('lastName', e.target.value)} /></label>
          <label>Email<input type="email" value={data.email} onChange={e => onChange('email', e.target.value)} /></label>
          <label>Phone<input type="tel" value={data.phone} onChange={e => onChange('phone', e.target.value)} /></label>
          <label>Congregation
            <select value={data.congregation} onChange={e => onChange('congregation', e.target.value)}>
              <option value="">Select...</option>
              {congregations.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>Type
            <select value={data.volunteerType || 'local'} onChange={e => onChange('volunteerType', e.target.value)}>
              <option value="local">Local</option>
              <option value="smpw">SMPW</option>
            </select>
          </label>
          <label>Portal Role
            <select value={data.role || ''} onChange={e => onChange('role', e.target.value)}>
              <option value="">Volunteer (default)</option>
              <option value="cart-volunteer">Cart Volunteer</option>
              <option value="driver">Driver</option>
              <option value="both">Cart + Driver</option>
              <option value="cart-admin">Cart Admin</option>
              <option value="driver-admin">Driver Admin</option>
              <option value="smpw-admin">SMPW Admin</option>
              <option value="superuser">Superuser</option>
            </select>
          </label>
        </div>
        <label className="roster-checkbox-label">
          <input type="checkbox" checked={!!data.isKeyman} onChange={e => onChange('isKeyman', e.target.checked)} />
          Keyman / Key Volunteer
        </label>
        <label className="roster-checkbox-label" style={{ marginTop: '6px' }}>
          <input type="checkbox" checked={!!data.isDeafHH} onChange={e => onChange('isDeafHH', e.target.checked)} />
          Deaf / Hard of Hearing — do not call, use email/text only
        </label>
      </div>

      <div className="roster-edit-section">
        <h4>Availability</h4>
        <div className="roster-avail-grid">
          {DAYS.map(d => (
            <DayAvailEditor
              key={d.key}
              label={d.label}
              value={data[d.field]}
              onChange={val => onChange(d.field, val)}
              dayField={d.field}
            />
          ))}
        </div>
      </div>

      <div className="roster-edit-section">
        <h4>Optional</h4>
        <div className="roster-edit-grid">
          <label>Marital Status
            <select value={data.maritalStatus || ''} onChange={e => onChange('maritalStatus', e.target.value)}>
              <option value="">Select...</option>
              <option value="Single">Single</option>
              <option value="Married">Married</option>
            </select>
          </label>
          <label>Family Member Name<input value={data.spouseName || ''} onChange={e => onChange('spouseName', e.target.value)} placeholder="Spouse, sibling, etc." /></label>
          <label>Emergency Contact<input value={data.emergencyContactName || ''} onChange={e => onChange('emergencyContactName', e.target.value)} /></label>
          <label>Emergency Phone<input type="tel" value={data.emergencyContactPhone || ''} onChange={e => onChange('emergencyContactPhone', e.target.value)} /></label>
        </div>
        <label style={{ display: 'block', marginTop: '10px' }}>
          Notes
          <textarea
            value={data.notes || ''}
            onChange={e => onChange('notes', e.target.value)}
            rows={3}
            placeholder="Internal notes about this volunteer…"
            style={{ width: '100%', marginTop: '4px', padding: '6px 8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '13px', resize: 'vertical' }}
          />
        </label>
      </div>
    </div>
  );
}

function DriverForm({ data, onChange, congregations }) {
  return (
    <div className="roster-edit-form">
      <div className="roster-edit-section">
        <h4>Basic Info</h4>
        <div className="roster-edit-grid">
          <label>First Name *<input value={data.firstName} onChange={e => onChange('firstName', e.target.value)} /></label>
          <label>Last Name *<input value={data.lastName} onChange={e => onChange('lastName', e.target.value)} /></label>
          <label>Email<input type="email" value={data.email} onChange={e => onChange('email', e.target.value)} /></label>
          <label>Phone<input type="tel" value={data.phone} onChange={e => onChange('phone', e.target.value)} /></label>
          <label>Congregation
            <select value={data.congregation} onChange={e => onChange('congregation', e.target.value)}>
              <option value="">Select...</option>
              {congregations.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
        </div>
      </div>

      <div className="roster-edit-section">
        <h4>Vehicle</h4>
        <div className="roster-edit-grid">
          <label>Make
            <select value={data.vehicleMake || ''} onChange={e => { onChange('vehicleMake', e.target.value); onChange('vehicleModel', ''); }}>
              <option value="">Select make…</option>
              {[...Object.keys(VEHICLE_MAKES_MODELS).filter(m => m !== 'Other').sort(), 'Other'].map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label>Model
            <select value={data.vehicleModel || ''} onChange={e => onChange('vehicleModel', e.target.value)} disabled={!data.vehicleMake}>
              <option value="">{data.vehicleMake ? 'Select model…' : 'Select make first'}</option>
              {[...(VEHICLE_MAKES_MODELS[data.vehicleMake] || [])].sort().map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label>Color
            <select value={data.vehicleColor || ''} onChange={e => onChange('vehicleColor', e.target.value)}>
              <option value="">Select color…</option>
              {[...VEHICLE_COLORS.filter(c => c !== 'Other').sort(), 'Other'].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>Vehicle Type
            <select value={data.vehicleType || ''} onChange={e => onChange('vehicleType', e.target.value)}>
              <option value="">Select type…</option>
              <option value="SUV">SUV</option>
              <option value="Minivan">Minivan</option>
              <option value="Van">Van</option>
              <option value="Sedan">Sedan</option>
              <option value="Hatchback">Hatchback</option>
              <option value="Truck / Pickup">Truck / Pickup</option>
              <option value="Crossover">Crossover</option>
              <option value="Wagon">Wagon</option>
              <option value="Other">Other</option>
            </select>
          </label>
          <label>Passengers (not incl. driver)<input type="number" min="1" max="15" value={data.passengerCapacity || ''} onChange={e => onChange('passengerCapacity', e.target.value)} /></label>
          <label>Cart Capacity<input type="number" min="0" max="10" value={data.cartCapacity || ''} onChange={e => onChange('cartCapacity', e.target.value)} /></label>
          <label>Also doing cart shifts?
            <select value={data.alsoDoingCartShifts || ''} onChange={e => onChange('alsoDoingCartShifts', e.target.value)}>
              <option value="">Select...</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </label>
        </div>
      </div>

      <div className="roster-edit-section">
        <h4>Availability</h4>
        <div className="roster-avail-grid">
          {DAYS.map(d => (
            <DayAvailEditor
              key={d.key}
              label={d.label}
              value={data[d.field]}
              onChange={val => onChange(d.field, val)}
              dayField={d.field}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── CSV Export ─────────────────────────────────────────────────────────────────

function exportToCSV(data, filename) {
  if (data.length === 0) return;
  const skip = new Set(['id']);
  const headers = Object.keys(data[0]).filter(k => !skip.has(k));
  const csvContent = [
    headers.join(','),
    ...data.map(row =>
      headers.map(h => `"${(row[h] ?? '').toString().replace(/"/g, '""')}"`).join(',')
    )
  ].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

// ── Sort header ────────────────────────────────────────────────────────────────

function SortHeader({ label, field, sortBy, sortDir, onSort, className }) {
  const active = sortBy === field;
  return (
    <div
      className={`${className} roster-sort-header${active ? ' sort-active' : ''}`}
      onClick={() => onSort(field)}
      title={`Sort by ${label}`}
    >
      {label}
      <span className="sort-indicator">{active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}</span>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

function RosterPage({ onLogout }) {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => searchParams.get('tab') === 'drivers' ? 'drivers' : 'volunteers');
  const [volunteers, setVolunteers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [congregations, setCongregations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter state
  const [search, setSearch] = useState('');
  const [congFilter, setCongFilter] = useState('');
  const [availDayFilter, setAvailDayFilter] = useState(''); // e.g. 'thu'
  const [availValueFilter, setAvailValueFilter] = useState('');
  const [notesFilter, setNotesFilter] = useState(false);

  // Sort state
  const [sortBy, setSortBy] = useState('lastName'); // 'lastName' | 'congregation' | 'thuAvailability' | etc.
  const [sortDir, setSortDir] = useState('asc');

  // Editing state
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  // Bulk delete state
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [deleteCong, setDeleteCong] = useState('');

  // Approval state
  const [approvingId, setApprovingId] = useState(null);
  const [bulkApproving, setBulkApproving] = useState(false);

  // Convert volunteer → driver
  const [addingAsDriverId, setAddingAsDriverId] = useState(null);
  const [addedAsDriverIds, setAddedAsDriverIds] = useState(new Set());

  const handleAddAsDriver = async (vol) => {
    if (addingAsDriverId) return;
    const existing = drivers.find(d =>
      d.firstName === vol.firstName && d.lastName === vol.lastName &&
      (d.email === vol.email || (!d.email && !vol.email))
    );
    if (existing) {
      alert(`${vol.firstName} ${vol.lastName} already exists in the driver roster.`);
      return;
    }
    setAddingAsDriverId(vol.id);
    await addDriver({
      ...BLANK_DRIVER,
      firstName: vol.firstName || '',
      lastName: vol.lastName || '',
      email: vol.email || '',
      phone: vol.phone || '',
      congregation: vol.congregation || '',
      alsoDoingCartShifts: 'Yes',
    });
    const updatedDrivers = await getDrivers();
    setDrivers(updatedDrivers);
    setAddedAsDriverIds(prev => new Set(prev).add(vol.id));
    setAddingAsDriverId(null);
  };

  // Adding state
  const [showAdd, setShowAdd] = useState(false);
  const [addData, setAddData] = useState({ ...BLANK_VOLUNTEER });
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    const [vols, drvs, congs] = await Promise.all([
      getVolunteers(),
      getDrivers(),
      getCongregations(),
    ]);
    setVolunteers(vols);
    setDrivers(drvs);
    setCongregations(congs.filter(c => c.active).map(c => c.name).sort());
    setLoading(false);
  };

  // ── Sort handler ─────────────────────────────────────────────────────────

  const handleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('asc'); }
  };

  // ── Filtering + Sorting ──────────────────────────────────────────────────

  const filterAndSort = (list) => {
    let result = list;

    if (congFilter) {
      result = result.filter(p => p.congregation === congFilter);
    }

    if (availDayFilter && availValueFilter) {
      const field = DAYS.find(d => d.key === availDayFilter)?.field;
      if (field) result = result.filter(p => {
        const val = p[field];
        if (availValueFilter === 'Anytime') return val === 'Anytime';
        if (availValueFilter === 'Not Available') return !val || val === '' || val === 'Not Available' || (Array.isArray(val) && val.length === 0);
        // specific shift time
        if (val === 'Anytime') return true;
        if (Array.isArray(val)) return val.includes(availValueFilter);
        return false;
      });
    }

    if (notesFilter) {
      result = result.filter(p => p.notes && p.notes.trim() !== '');
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(p =>
        `${p.firstName} ${p.lastName}`.toLowerCase().includes(q) ||
        (p.email || '').toLowerCase().includes(q) ||
        (p.phone || '').includes(q)
      );
    }

    return [...result].sort((a, b) => {
      let aVal, bVal;
      if (sortBy === 'congregation') {
        aVal = a.congregation || '';
        bVal = b.congregation || '';
      } else if (sortBy.endsWith('Availability')) {
        // sort by number of available shifts (more = first when asc)
        const aO = availCount(a[sortBy]);
        const bO = availCount(b[sortBy]);
        const diff = bO - aO; // more shifts = lower sort position (shows first)
        if (diff !== 0) return sortDir === 'asc' ? diff : -diff;
        aVal = a.lastName || '';
        bVal = b.lastName || '';
      } else {
        aVal = a.lastName || '';
        bVal = b.lastName || '';
      }
      const cmp = aVal.localeCompare(bVal);
      if (cmp !== 0) return sortDir === 'asc' ? cmp : -cmp;
      return (a.firstName || '').localeCompare(b.firstName || '');
    });
  };

  const isVolunteers = tab === 'volunteers';
  const fullList = isVolunteers ? volunteers : drivers;
  const list = filterAndSort(fullList);

  const congOptions = [...new Set(fullList.map(p => p.congregation).filter(Boolean))].sort();

  const clearFilters = () => { setSearch(''); setCongFilter(''); setAvailDayFilter(''); setAvailValueFilter(''); setNotesFilter(false); };
  const isFiltered = !!congFilter || !!search.trim() || (!!availDayFilter && !!availValueFilter) || notesFilter;

  // ── Edit handlers ────────────────────────────────────────────────────────

  const startEdit = (person) => { setEditingId(person.id); setEditData({ ...person }); setShowAdd(false); };
  const cancelEdit = () => { setEditingId(null); setEditData({}); };
  const changeEdit = (field, value) => setEditData(prev => ({ ...prev, [field]: value }));

  const saveEdit = async () => {
    if (!editData.firstName || !editData.lastName) return alert('First and last name are required.');
    setSaving(true);
    const fn = isVolunteers ? updateVolunteer : updateDriver;
    const { id, createdAt, ...rest } = editData;
    await fn(editingId, rest);
    if (isVolunteers) setVolunteers(prev => prev.map(v => v.id === editingId ? { ...v, ...rest } : v));
    else setDrivers(prev => prev.map(d => d.id === editingId ? { ...d, ...rest } : d));
    setSaving(false);
    setEditingId(null);
  };

  // ── Delete handlers ──────────────────────────────────────────────────────

  const handleDelete = async (id, name) => {
    const confirmed = window.prompt(`Type "DELETE" to confirm deleting ${name}:`);
    if (confirmed !== 'DELETE') { if (confirmed !== null) alert('Confirmation text did not match. No changes made.'); return; }
    const fn = isVolunteers ? deleteVolunteer : deleteDriver;
    await fn(id);
    if (isVolunteers) setVolunteers(prev => prev.filter(v => v.id !== id));
    else setDrivers(prev => prev.filter(d => d.id !== id));
    if (editingId === id) cancelEdit();
  };

  const handleDeleteByCong = async (congName) => {
    const targets = fullList.filter(p => p.congregation === congName);
    if (targets.length === 0) return;
    const confirmed = window.prompt(`Type "DELETE" to confirm deleting all ${targets.length} ${tab} from "${congName}":`);
    if (confirmed !== 'DELETE') { if (confirmed !== null) alert('Confirmation text did not match. No changes made.'); return; }
    const fn = isVolunteers ? deleteVolunteer : deleteDriver;
    for (const p of targets) await fn(p.id);
    if (isVolunteers) setVolunteers(prev => prev.filter(v => v.congregation !== congName));
    else setDrivers(prev => prev.filter(d => d.congregation !== congName));
    if (targets.some(p => p.id === editingId)) cancelEdit();
  };

  const handleDeleteAll = async () => {
    if (fullList.length === 0) return;
    const confirmed = window.prompt(
      `This will permanently delete ALL ${fullList.length} ${tab}.\n\nType "DELETE ALL" to confirm:`
    );
    if (confirmed !== 'DELETE ALL') { if (confirmed !== null) alert('Confirmation text did not match. No changes made.'); return; }
    const fn = isVolunteers ? deleteVolunteer : deleteDriver;
    for (const p of fullList) await fn(p.id);
    if (isVolunteers) setVolunteers([]);
    else setDrivers([]);
    cancelEdit();
  };

  // ── Add handlers ─────────────────────────────────────────────────────────

  const changeAdd = (field, value) => setAddData(prev => ({ ...prev, [field]: value }));

  const handleAdd = async () => {
    if (!addData.firstName || !addData.lastName) { setAddError('First and last name are required.'); return; }
    setAdding(true); setAddError('');
    const fn = isVolunteers ? addVolunteer : addDriver;
    const result = await fn(addData);
    if (result.success) {
      await loadAll();
      setAddData(isVolunteers ? { ...BLANK_VOLUNTEER } : { ...BLANK_DRIVER });
      setShowAdd(false);
    } else {
      setAddError(result.error || 'Failed to add. Please try again.');
    }
    setAdding(false);
  };

  const openAdd = () => {
    setShowAdd(s => !s);
    setAddData(isVolunteers ? { ...BLANK_VOLUNTEER } : { ...BLANK_DRIVER });
    setAddError('');
    setEditingId(null);
  };

  // ── Export ────────────────────────────────────────────────────────────────

  const handleExport = () => {
    let label = 'all';
    if (congFilter) label = congFilter.replace(/\s+/g, '-');
    else if (availDayFilter && availValueFilter) label = `${availDayFilter}-${availValueFilter.replace(/\s+/g, '-')}`;
    else if (search.trim()) label = 'filtered';
    exportToCSV(list, `${tab}-${label}-${new Date().toISOString().split('T')[0]}.csv`);
  };

  // ── Approval handlers ────────────────────────────────────────────────────

  const handleSetApproval = async (id, status) => {
    setApprovingId(id);
    const data = {
      approvalStatus: status,
      approvedBy: 'admin',
      approvedAt: status === 'approved' ? new Date().toISOString() : null,
      approvalNotes: null,
    };
    await updateVolunteerApproval(id, data);
    setVolunteers(prev => prev.map(v => v.id === id ? { ...v, ...data } : v));
    setApprovingId(null);
  };

  const handleBulkApprove = async () => {
    const pending = volunteers.filter(v => !v.approvalStatus || v.approvalStatus === 'pending');
    if (pending.length === 0) return;
    setBulkApproving(true);
    const now = new Date().toISOString();
    for (const v of pending) {
      await updateVolunteerApproval(v.id, { approvalStatus: 'approved', approvedBy: 'admin', approvedAt: now, approvalNotes: null });
    }
    setVolunteers(prev => prev.map(v =>
      (!v.approvalStatus || v.approvalStatus === 'pending')
        ? { ...v, approvalStatus: 'approved', approvedBy: 'admin', approvedAt: now }
        : v
    ));
    setBulkApproving(false);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  // Pre-compute set of "firstName|lastName" keys that already have a driver profile
  const driverNameKeys = new Set(drivers.map(d => `${d.firstName}|${d.lastName}`));

  const renderRow = (person) => {
    const isEditing = editingId === person.id;
    const name = `${person.lastName || '–'}, ${person.firstName || '–'}`;
    const alreadyDriver = driverNameKeys.has(`${person.firstName}|${person.lastName}`) || addedAsDriverIds.has(person.id);

    return (
      <div key={person.id} className={`roster-row${isEditing ? ' roster-row-editing' : ''}`}>
        <div className="roster-row-summary">
          <div className="roster-col roster-col-name">
            <span className="roster-name">{name}</span>
            {person.volunteerType === 'smpw' && <span className="roster-badge badge-smpw">SMPW</span>}
            {person.isKeyman && <span className="roster-badge badge-key">🔑</span>}
            {person.isDeafHH && <span className="roster-badge badge-deaf" title="Deaf / Hard of Hearing — email/text only">🦻 D/HH</span>}
            {person.vehicleType && <span className="roster-badge badge-vehicle">{person.vehicleType}</span>}
            {person.role && person.role !== 'cart-volunteer' && (
              <span className="roster-badge badge-role" title={`Portal role: ${person.role}`}>{person.role}</span>
            )}
            {person.notes && (
              <span className="roster-notes-preview" title={person.notes}>
                {person.notes.length > 50 ? person.notes.slice(0, 50) + '…' : person.notes}
              </span>
            )}
          </div>
          <div className="roster-col roster-col-cong">{person.congregation || '—'}</div>
          <div className="roster-col roster-col-phone">
            {person.phone ? <a href={`tel:${person.phone}`}>{person.phone}</a> : '—'}
          </div>
          <div className="roster-col roster-col-email">{person.email || '—'}</div>
          <div className="roster-col roster-col-avail">
            {DAYS.map(d => <AvailCell key={d.key} val={person[d.field]} />)}
          </div>
          {isVolunteers && (
            <div className="roster-col roster-col-approval">
              {(() => {
                const status = person.approvalStatus;
                const busy = approvingId === person.id;
                if (status === 'approved') return (
                  <span className="roster-approval-badge approved">
                    Approved
                    <button className="roster-approval-revoke" onClick={() => handleSetApproval(person.id, 'denied')} disabled={busy} title="Revoke approval">✕</button>
                  </span>
                );
                if (status === 'denied') return (
                  <span className="roster-approval-badge denied">
                    Denied
                    <button className="roster-approval-revoke" onClick={() => handleSetApproval(person.id, 'approved')} disabled={busy} title="Approve">✓</button>
                  </span>
                );
                if (!person.authUid) return <span className="roster-approval-badge unlinked">Not linked</span>;
                return (
                  <span className="roster-approval-badge pending">
                    Pending
                    <button className="roster-approval-approve" onClick={() => handleSetApproval(person.id, 'approved')} disabled={busy} title="Approve">✓</button>
                    <button className="roster-approval-revoke" onClick={() => handleSetApproval(person.id, 'denied')} disabled={busy} title="Deny">✕</button>
                  </span>
                );
              })()}
            </div>
          )}
          <div className="roster-col roster-col-actions">
            <button className="roster-btn-edit" onClick={() => isEditing ? cancelEdit() : startEdit(person)}>
              {isEditing ? 'Cancel' : 'Edit'}
            </button>
            {isVolunteers && !alreadyDriver && (
              <button
                className="roster-btn-icon"
                onClick={() => handleAddAsDriver(person)}
                disabled={addingAsDriverId === person.id}
                title={addingAsDriverId === person.id ? 'Adding driver profile…' : 'Create driver profile'}
              >
                {addingAsDriverId === person.id ? '…' : '🚗'}
              </button>
            )}
            <button className="roster-btn-delete" onClick={() => handleDelete(person.id, name)}>Delete</button>
          </div>
        </div>

        {isEditing && (
          <div className="roster-edit-panel">
            {isVolunteers
              ? <VolunteerForm data={editData} onChange={changeEdit} congregations={congregations} />
              : <DriverForm data={editData} onChange={changeEdit} congregations={congregations} />
            }
            <div className="roster-edit-actions">
              <button className="btn btn-primary" onClick={saveEdit} disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              <button className="btn btn-secondary" onClick={cancelEdit}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="admin-container"><AdminNav onLogout={onLogout} /><div className="loading">Loading…</div></div>;
  }

  const countLabel = isFiltered
    ? `${list.length} of ${fullList.length} ${tab}`
    : `${fullList.length} ${tab}`;

  return (
    <div className="admin-container">
      <AdminNav onLogout={onLogout} />
      <header className="admin-header">
        <h1>Volunteers</h1>
        <p className="page-subtitle">View, search, add, edit, and remove volunteers and drivers.</p>
      </header>

      <div className="roster-content">
        {/* Tab bar */}
        <div className="roster-tabs">
          <button className={`roster-tab${tab === 'volunteers' ? ' active' : ''}`} onClick={() => { setTab('volunteers'); setEditingId(null); setShowAdd(false); clearFilters(); }}>
            Volunteers ({volunteers.length})
          </button>
          <button className={`roster-tab${tab === 'drivers' ? ' active' : ''}`} onClick={() => { setTab('drivers'); setEditingId(null); setShowAdd(false); clearFilters(); }}>
            Drivers ({drivers.length})
          </button>
        </div>

        {/* Filter panel */}
        <div className="roster-filter-section">
          <div className="roster-filter-header">
            <span className="roster-filter-title">Filters</span>
            <div className="roster-filter-count">
              <span className="roster-count-label">{countLabel}</span>
              {isFiltered && (
                <button className="roster-clear-filters" onClick={clearFilters}>✕ Clear filters</button>
              )}
            </div>
          </div>

          {/* Row 1: Search, Congregation, Sort */}
          <div className="roster-filter-row">
            <div className="roster-filter-group">
              <label className="roster-filter-label">Search</label>
              <input
                className="roster-search"
                type="text"
                placeholder="Name, phone, or email…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className="roster-filter-group">
              <label className="roster-filter-label">Congregation</label>
              <select className="roster-cong-filter" value={congFilter} onChange={e => setCongFilter(e.target.value)}>
                <option value="">All congregations</option>
                {congOptions.map(c => {
                  const cnt = fullList.filter(p => p.congregation === c).length;
                  return <option key={c} value={c}>{c} ({cnt})</option>;
                })}
              </select>
            </div>
            <div className="roster-filter-group">
              <label className="roster-filter-label">Sort by</label>
              <div style={{ display: 'flex', gap: '6px' }}>
                <select
                  value={sortBy}
                  onChange={e => { setSortBy(e.target.value); setSortDir('asc'); }}
                  className="roster-cong-filter"
                >
                  <option value="lastName">Name</option>
                  <option value="congregation">Congregation</option>
                  {DAYS.map(d => <option key={d.field} value={d.field}>{d.label} avail.</option>)}
                </select>
                <button
                  className="roster-sort-dir-btn"
                  onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                  title={sortDir === 'asc' ? 'Ascending — click for descending' : 'Descending — click for ascending'}
                >
                  {sortDir === 'asc' ? '▲' : '▼'}
                </button>
              </div>
            </div>
          </div>

          {/* Row 2: Availability Day + Shift (paired — shift is disabled until day is chosen) */}
          <div className="roster-filter-row roster-filter-row-avail">
            <div className="roster-filter-group">
              <label className="roster-filter-label">Filter by Availability Day</label>
              <select
                value={availDayFilter}
                onChange={e => { setAvailDayFilter(e.target.value); setAvailValueFilter(''); }}
                className="roster-cong-filter"
              >
                <option value="">Any day</option>
                {DAYS.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
              </select>
            </div>
            <div className="roster-filter-group">
              <label className="roster-filter-label">
                Shift / Status
                {!availDayFilter && <span style={{ color: '#94a3b8', fontWeight: 400, marginLeft: 4 }}>(select a day first)</span>}
              </label>
              <select
                value={availValueFilter}
                onChange={e => setAvailValueFilter(e.target.value)}
                className="roster-cong-filter"
                disabled={!availDayFilter}
              >
                <option value="">Any</option>
                <option value="Anytime">Anytime</option>
                {SHIFT_TIMES.map(s => <option key={s} value={s}>{s}</option>)}
                <option value="Not Available">Not Available</option>
              </select>
            </div>
          </div>

          {/* Row 3: Notes filter */}
          <div className="roster-filter-row">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#374151' }}>
              <input
                type="checkbox"
                checked={notesFilter}
                onChange={e => setNotesFilter(e.target.checked)}
              />
              Only show volunteers/drivers with notes
            </label>
          </div>
        </div>

        {/* Actions bar */}
        <div className="roster-count-bar">
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            <button className="btn btn-secondary" onClick={handleExport} title={isFiltered ? 'Export filtered results' : 'Export all'}>
              Export CSV{isFiltered ? ` (${list.length})` : ''}
            </button>
            <button className={`btn${showAdd ? ' btn-secondary' : ' btn-primary'}`} onClick={openAdd}>
              {showAdd ? '✕ Cancel' : `+ Add ${isVolunteers ? 'Volunteer' : 'Driver'}`}
            </button>
            <button className="btn roster-bulk-delete-btn" onClick={() => { setShowBulkDelete(s => !s); setDeleteCong(''); setShowAdd(false); }}>
              {showBulkDelete ? '✕ Cancel' : 'Bulk Delete'}
            </button>
          </div>
        </div>

        {/* Bulk delete panel */}
        {showBulkDelete && (
          <div className="roster-bulk-delete-panel">
            <h4>Bulk Delete</h4>
            <div className="roster-bulk-delete-row">
              <label>Delete by congregation:</label>
              <select value={deleteCong} onChange={e => setDeleteCong(e.target.value)}>
                <option value="">Select congregation…</option>
                {[...new Set(fullList.map(p => p.congregation).filter(Boolean))].sort()
                  .map(c => {
                    const count = fullList.filter(p => p.congregation === c).length;
                    return <option key={c} value={c}>{c} ({count})</option>;
                  })}
              </select>
              <button className="btn roster-danger-btn" disabled={!deleteCong} onClick={() => { handleDeleteByCong(deleteCong); setDeleteCong(''); }}>
                Delete all from this congregation
              </button>
            </div>
            <div className="roster-bulk-delete-row">
              <label>Delete all {tab}:</label>
              <button className="btn roster-danger-btn" onClick={handleDeleteAll}>
                Delete All {fullList.length} {tab}
              </button>
            </div>
          </div>
        )}

        {/* Add form */}
        {showAdd && (
          <div className="roster-add-panel">
            <h3>Add New {isVolunteers ? 'Volunteer' : 'Driver'}</h3>
            {isVolunteers
              ? <VolunteerForm data={addData} onChange={changeAdd} congregations={congregations} isAdd />
              : <DriverForm data={addData} onChange={changeAdd} congregations={congregations} />
            }
            {addError && <div className="error-message" style={{ margin: '0 0 12px' }}>{addError}</div>}
            <div className="roster-edit-actions">
              <button className="btn btn-primary" onClick={handleAdd} disabled={adding}>
                {adding ? 'Adding…' : `Add ${isVolunteers ? 'Volunteer' : 'Driver'}`}
              </button>
              <button className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
            </div>
          </div>
        )}

        {/* Bulk approve bar — volunteers tab only */}
        {isVolunteers && (() => {
          const pendingCount = volunteers.filter(v => v.authUid && (!v.approvalStatus || v.approvalStatus === 'pending')).length;
          if (pendingCount === 0) return null;
          return (
            <div className="roster-bulk-approve-bar">
              <span>{pendingCount} volunteer{pendingCount !== 1 ? 's' : ''} linked and awaiting approval</span>
              <button className="btn btn-primary" onClick={handleBulkApprove} disabled={bulkApproving}>
                {bulkApproving ? 'Approving…' : `Approve All Pending (${pendingCount})`}
              </button>
            </div>
          );
        })()}

        {/* Column headers */}
        <div className="roster-header-row">
          <SortHeader label="Name" field="lastName" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} className="roster-col roster-col-name" />
          <SortHeader label="Congregation" field="congregation" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} className="roster-col roster-col-cong" />
          <div className="roster-col roster-col-phone">Phone</div>
          <div className="roster-col roster-col-email">Email</div>
          <div className="roster-col roster-col-avail" style={{ display: 'flex', gap: '2px' }}>
            {DAYS.map(d => (
              <SortHeader key={d.field} label={d.key.charAt(0).toUpperCase() + d.key.slice(1)} field={d.field} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} className="" />
            ))}
          </div>
          {isVolunteers && <div className="roster-col roster-col-approval">Portal</div>}
          <div className="roster-col roster-col-actions"></div>
        </div>

        {/* Rows */}
        {list.length === 0 ? (
          <div className="no-data">
            {isFiltered ? 'No results match your filters.' : `No ${tab} yet.`}
          </div>
        ) : (
          list.map(renderRow)
        )}
      </div>
    </div>
  );
}

export default RosterPage;
